import { moNum } from "@/lib/moNum";
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Pencil } from "lucide-react";
import CreateMOPopover from "@/components/CreateMOPopover";
import EditMODialog from "@/components/EditMODialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const num = (n) => Math.round(n).toLocaleString();
const fmtQ = (n) => Number(Math.round(n * 100) / 100).toLocaleString();

// Truckloads · Pallets / Cases · Bottles breakdown, matching the Production Schedule's Ordered column.
function OrderedBreakdown({ o }) {
  const bottles = Number(o.remaining_bottles) || 0;
  const cp = Number(o.case_pack) || 0;
  const bpp = Number(o.bottles_per_pallet) || 0;
  const ppt = Number(o.pallets_per_truckload) || 0;
  if (!bottles) return <span>—</span>;
  const cases = cp ? bottles / cp : null;
  const pallets = bpp ? bottles / bpp : null;
  const trucks = ppt && pallets != null ? pallets / ppt : null;
  const top = [
    trucks != null ? `${fmtQ(trucks)} Truckload${trucks === 1 ? "" : "s"}` : null,
    pallets != null ? `${fmtQ(pallets)} Pallet${pallets === 1 ? "" : "s"}` : null,
  ].filter(Boolean).join(" · ");
  const bottom = [
    cases != null ? `${fmtQ(cases)} Case${cases === 1 ? "" : "s"}` : null,
    `${num(bottles)} Bottles`,
  ].filter(Boolean).join(" · ");
  return (
    <span className="whitespace-nowrap">
      {top && <span className="block text-xs text-slate-500">{top}</span>}
      <span className="block">{bottom}</span>
    </span>
  );
}

export function ReservationsDialog({ resvKey, resvName, reservations, stock = 0, onOrderQty = 0, onClose, products = [], components = [], onChanged, mode = "" }) {
  const [editMo, setEditMo] = useState(null);
  const openEdit = async (o) => {
    try {
      const { data } = await api.get(`/manufacturing-orders/${o.mo_id}`);
      setEditMo(data);
    } catch { toast.error("Failed to open the order for editing"); }
  };
  const confirmProjected = async (o) => {
    try {
      await api.post(`/manufacturing-orders/${o.mo_id}/projected`, { projected: false });
      toast.success(`${moNum(o.mwo_number)} confirmed — now a real production order`);
      onChanged?.();
    } catch { toast.error("Failed to confirm the projected order"); }
  };
  const removeProjected = async (o) => {
    try {
      await api.delete(`/manufacturing-orders/${o.mo_id}`);
      toast.success(`${moNum(o.mwo_number)} projection removed — reservation released`);
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e, "Failed to remove the projected order")); }
  };
  const resvOrders = resvKey ? reservations[resvKey]?.orders || [] : [];
  const reserved = resvKey ? reservations[resvKey]?.reserved || 0 : 0;
  const projResv = Math.round(resvOrders.filter((o) => o.projected).reduce((s, o) => s + (o.reserved || 0), 0) * 100) / 100;
  const salesResv = Math.round((reserved - projResv) * 100) / 100;
  const shownOrders = mode === "sales" ? resvOrders.filter((o) => !o.projected)
    : mode === "projections" ? resvOrders.filter((o) => o.projected) : resvOrders;
  const shownTotal = mode === "sales" ? salesResv : mode === "projections" ? projResv : reserved;
  const title = mode === "sales" ? "Reserved for Sales" : mode === "projections" ? "Reserved for Projections" : "Reserved for Production";
  const desc = mode === "sales" ? "committed to sales-driven manufacturing orders (not yet consumed)."
    : mode === "projections" ? "committed to projected manufacturing orders (not yet confirmed or consumed)."
    : "committed to active manufacturing orders (not yet consumed).";
  const available = stock - reserved;
  const projected = stock + onOrderQty - reserved;
  const stat = (label, value, testid, alwaysDark = false) => (
    <div className="border border-slate-200 px-3 py-2" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-500">{label}</p>
      <p className={`mt-0.5 tabular-nums text-sm font-bold ${value < 0 ? "text-red-600" : alwaysDark ? "text-slate-900" : "text-slate-700"}`}>{num(value)}</p>
    </div>
  );
  return (
    <Dialog open={!!resvKey} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-sm sm:max-w-4xl" data-testid="reservations-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{title}</DialogTitle>
          <DialogDescription>{resvName} — {desc}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="reservations-stats">
          {stat("On Order", onOrderQty, "resv-stat-on-order")}
          {stat("Units in Stock", stock, "resv-stat-stock", true)}
          {mode !== "projections" && stat("Reserved for Sales", salesResv, "resv-stat-reserved", true)}
          {mode !== "sales" && stat("Reserved for Projections", projResv, "resv-stat-reserved-proj", true)}
          {stat("Available After Projections", available, "resv-stat-available", true)}
          {stat("Available After Purchases", projected, "resv-stat-projected")}
        </div>
        {shownOrders.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400">No active reservations.</p>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm" data-testid="reservations-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.08em] text-slate-500">
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">MO</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Production Date</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Product / Design</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Produce For</th>
                <th className="border-b border-slate-200 px-2 py-2 text-left font-semibold">Remaining to Produce</th>
                <th className="border-b border-slate-200 px-2 py-2 text-right font-semibold">Reserved</th>
              </tr>
            </thead>
            <tbody>
              {shownOrders.map((o, i) => (
                <tr key={`${moNum(o.mwo_number) || "mwo"}-${i}`} className="text-slate-700" data-testid={`reservation-order-${i}`}>
                  <td className="border-b border-slate-100 px-2 py-2 font-semibold text-slate-900 whitespace-nowrap">
                    {moNum(o.mwo_number) || "—"}
                    {o.mo_id && (
                      <button type="button" onClick={() => openEdit(o)} title="Edit this manufacturing order"
                        className="ml-1.5 inline-flex align-middle text-slate-400 hover:text-[#002FA7]" data-testid={`reservation-edit-${i}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {o.projected && (
                      <>
                        <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700" data-testid={`reservation-projected-${i}`}>Projected</span>
                        <button type="button" onClick={() => confirmProjected(o)} className="ml-1.5 text-[10px] font-semibold text-[#002FA7] hover:underline" data-testid={`reservation-confirm-${i}`}>Confirm</button>
                        <button type="button" onClick={() => removeProjected(o)} className="ml-1.5 text-[10px] font-semibold text-red-600 hover:underline" title="Delete this projected order and release its reservation" data-testid={`reservation-remove-${i}`}>Remove</button>
                      </>
                    )}
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2 whitespace-nowrap">{o.production_date || "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2">
                    <span className="block">{o.product_name || "—"}</span>
                    <span className="block text-xs text-slate-500">{[o.design_name, o.case_pack ? `${o.case_pack}pk` : null].filter(Boolean).join(" · ") || "—"}</span>
                  </td>
                  <td className="border-b border-slate-100 px-2 py-2" data-testid={`reservation-customer-${i}`}>{o.customer_name || "—"}</td>
                  <td className="border-b border-slate-100 px-2 py-2" data-testid={`reservation-ordered-${i}`}><OrderedBreakdown o={o} /></td>
                  <td className="border-b border-slate-100 px-2 py-2 text-right font-medium tabular-nums">{num(o.reserved)}</td>
                </tr>
              ))}
              <tr className="text-slate-900">
                <td className="px-2 py-2 text-xs font-semibold uppercase tracking-wide" colSpan={5}>
                  Total reserved
                  {!mode && (
                    <span className="ml-2 font-medium normal-case tracking-normal text-slate-500" data-testid="reservations-total-split">
                      {num(salesResv)} sales · {num(projResv)} projections
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-right font-bold tabular-nums" data-testid="reservations-total">{num(shownTotal)}</td>
              </tr>
            </tbody>
          </table>
          </div>
        )}
        {products.length > 0 && mode !== "sales" && (
          <div className="flex justify-end border-t border-slate-100 pt-3">
            <CreateMOPopover products={products} components={components} onPlaced={onChanged} projected triggerLabel="Add Projected Order" testId="add-projected-order" />
          </div>
        )}
        <EditMODialog mo={editMo} products={products} open={!!editMo}
          onOpenChange={(o) => { if (!o) setEditMo(null); }}
          onSaved={() => { setEditMo(null); onChanged?.(); }} />
      </DialogContent>
    </Dialog>
  );
}
